// @flow
import React, {FunctionComponent, useCallback, useState} from 'react';
import {Button, Chip, Stack} from "@mui/material";
import TabularTextCard from "./tabular-text-card";
import {useParams} from "react-router-dom";
import RegistrationReviewService from "../../services/registration-review-service";
import ExistingRegistrationsCard from "./existing-registrations-card";
import ReviewSimilaritiesCard from "./review-similarities-card";
import CommentCard from "./comment-card";
import OnCloseNavigationSnackbar from "../Common/on-close-navigation-snackbar";
import RouteService from "../../services/route-service";
import FlexStack from "../Common/flex-stack";
import {AnimalService} from "../../services/animal-service";
import {RegistrationService} from "../../services/registration-service";
import {JsonAnimal} from "../../api/generated/rest-dto";
import PedigreeCard from "../AnimalDashboard/Pedigree/pedigree-card";
import {useTranslation} from "react-i18next";
import FlexCard from "./flex-card";
import RenderOnRole from "../Permissions/render-on-role";
import {PANON_ADMIN, REGISTRY_WARDEN} from "../Permissions/Roles";
import {useSuspenseQuery} from "@tanstack/react-query";
import {Done, ThumbDown} from "@mui/icons-material";
import {NamedId} from "../../api/generated/lib-rest";
import {useNav} from "../Common/hooks/use-nav";
import IdentificationsWidget from "../AnimalDashboard/identifications-widget";

type AnimalReviewPathParams = {
    id: string;
};

const ACCEPTED_MESSAGE = 'Registrierung akzeptiert';
const REJECTED_MESSAGE = 'Registrierung abgelehnt';

const useReviewRequest = (reviewRequestId: string) => {
    const {data} = useSuspenseQuery({
        queryKey: ['review-request', reviewRequestId],
        queryFn: async () => {
            if (!reviewRequestId) {
                return {
                    reviewRequest: undefined,
                    similarAnimals: [],
                    animal: undefined,
                    registrations: [],
                };
            }

            const reviewRequest = await RegistrationService.getPendingRequest(reviewRequestId);
            const similarAnimals = await RegistrationReviewService.findRegisteredSimilarAnimals(reviewRequestId);
            const animal = await AnimalService.loadJsonAnimalByPanonId(reviewRequest.animal.id); //animal.id is a panonId
            const registrations = await RegistrationService.findRegistrationsByPanonId(reviewRequest.animal.id);

            return {
                reviewRequest,
                similarAnimals,
                animal,
                registrations,
            };
        },
    });

    return data;
};

const StatusCard = (props: { rejected: boolean; breeder: NamedId }) => {
    const nav = useNav();
    return (
        <FlexCard title={
            <>
                {'Anfragestatus '}
                {props.rejected
                    ? <Chip label="Abgelehnt" color="error"/>
                    : <Chip label="Aktiv" color="primary"/>}
            </>
        }>
            <Stack direction="row" alignItems="baseline">
                {'Eingereicht von '}
                <Button onClick={() => nav.push(RouteService.BREEDER_DETAILS, {breederId: props.breeder.id})}>
                    {props.breeder.name}
                </Button>
            </Stack>
        </FlexCard>
    );
}

const useSnackbar = () => {
    const [showSnackbar, setShowSnackbar] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState('');

    const showSnack = useCallback((message: string) => {
        setSnackbarMessage(message);
        setShowSnackbar(true);
    }, []);

    return {
        showSnackbar,
        snackbarMessage,
        showSnack,
    } as const;
};

const AnimalReview: FunctionComponent = () => {

    const {t} = useTranslation();

    const {showSnackbar, snackbarMessage, showSnack} = useSnackbar();

    const [comment, setComment] = useState('');

    const {id: reviewRequestId} = useParams<AnimalReviewPathParams>();

    const {reviewRequest, similarAnimals, animal, registrations} = useReviewRequest(reviewRequestId);
    const isRejected = !!reviewRequest?.rejectionTime;

    if (!reviewRequest) {
        return null;
    }

    const handleAccept = async () => {
        showSnack(ACCEPTED_MESSAGE);
        await RegistrationReviewService.acceptReview(reviewRequestId);
    }

    const handleReject = async () => {
        showSnack(REJECTED_MESSAGE)
        await RegistrationReviewService.rejectReview(reviewRequestId, comment);
    }

    const transformAnimal = (animal: JsonAnimal | undefined): Map<string, string> => {
        if (!animal) return new Map();
        return new Map([
            ['Herdkürzel', animal.herdCode],
            ['Name', animal.name],
            ['Geburtsdatum', animal.dateOfBirth.toLocaleString()],
            ['Geburtsland', animal.countryOfBirth],
            ['Rasse', t('breed.' + animal.breed)],
            ["Farbe", t('color.' + animal.fiberColor)],
            ["Zusatzfarben", animal.secondaryColors.map(c => t('color.' + c)).join(', ')],
            ["Augenfarbe", animal.eyeColor ? t('eyecolor.' + animal.eyeColor) : ''],
            ['Geschlecht', t('sex.' + animal.sex)],
            //["Züchter", baseData.breederName], //TODO implement as soon as we have ownership endpoints on the backend
            //["Besitzer", baseData.ownerName], //TODO implement as soon as we have ownership endpoints on the backend
        ]);
    }

    return (
        <FlexStack>
            <StatusCard rejected={isRejected} breeder={reviewRequest.breeder}/>
            <TabularTextCard title={AnimalService.getAnimalFullName(animal)} displayPairs={transformAnimal(animal)}/>
            <IdentificationsWidget animal={animal} disableAddNew/>
            <ExistingRegistrationsCard existingRegistrations={registrations}/>
            <ReviewSimilaritiesCard reviewId={reviewRequestId} reviewedAnimal={animal} similarAnimals={similarAnimals}/>
            <PedigreeCard animal={animal}/>
            {isRejected
                ? (
                    <FlexCard title="Kommentare">
                        {reviewRequest.rejectionComment}
                    </FlexCard>
                )
                : <RenderOnRole roles={[REGISTRY_WARDEN, PANON_ADMIN]}>
                    <CommentCard onCommentChange={setComment}/>
                    <Stack direction="row" spacing={1} display="flex" justifyContent="flex-end">
                        <Button onClick={() => handleReject()} color="error" variant="contained" size="large" startIcon={<ThumbDown/>}>
                            Ablehnen
                        </Button>
                        <Button onClick={() => handleAccept()} variant="contained" size="large" startIcon={<Done/>}>
                            Akzeptieren
                        </Button>
                    </Stack>
                </RenderOnRole>}
            <OnCloseNavigationSnackbar message={snackbarMessage} triggerSnackbar={showSnackbar}
                                       expandedRoute={RouteService.REGISTRY_ANIMAL_REVIEW_LIST}/>
        </FlexStack>
    );
};

export default AnimalReview
